import React from 'react'

/**
 * A function which returns the time until a certain date/time.
 *
 * @param dateTime - The target date/time to calculate time until
 * @returns - An object with the days, hours, minutes and seconds until the
 * target date
 */
const getTimeUntilDate = (dateTime: string) => {
    const targetDate = new Date(dateTime)
    const today = new Date()

    // Find the distance between now and the countdown date
    const total = targetDate.getTime() - today.getTime()

    // Time calculations for days, hours, minutes and seconds
    const days = Math.floor(total / (1000 * 60 * 60 * 24))
    const hours = Math.floor((total / (1000 * 60 * 60)) % 24)
    const minutes = Math.floor((total / 1000 / 60) % 60)
    const seconds = Math.floor((total / 1000) % 60)

    return {
        total,
        days,
        hours,
        minutes,
        seconds,
    }
}

/**
 * A hook which calculates the time until a certain date/time, updates every
 * second and returns the updated values.
 *
 * @param dateTime - The target date/time to count down to
 * @returns - An object with the days, hours, minutes and seconds until the
 * target date
 */
export const useCountdown = (dateTime: string) => {
    const initialValues = getTimeUntilDate(dateTime)

    const [days, setDays] = React.useState(Math.max(0, initialValues.days))
    const [hours, setHours] = React.useState(Math.max(0, initialValues.hours))
    const [minutes, setMinutes] = React.useState(Math.max(0, initialValues.minutes))
    const [seconds, setSeconds] = React.useState(Math.max(0, initialValues.seconds))

    React.useEffect(() => {
        const interval = setInterval(function () {
            const nextValues = getTimeUntilDate(dateTime)

            setDays(Math.max(0, nextValues.days))
            setHours(Math.max(0, nextValues.hours))
            setMinutes(Math.max(0, nextValues.minutes))
            setSeconds(Math.max(0, nextValues.seconds))

            if (nextValues.total <= 0) {
                clearInterval(interval)
            }
        }, 1000)
    }, [dateTime])

    return {
        days,
        hours,
        minutes,
        seconds,
    }
}
